import { useEffect, useRef, useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Volume2, Square, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function resolveAudio(text, language, provider, maxWaitMs) {
  let res = await apiClient.post("/tts", { text: text.slice(0, 4000), language, provider });
  const started = Date.now();
  while (res.data.status === "pending") {
    if (Date.now() - started > maxWaitMs) return null;
    await sleep(2000);
    res = await apiClient.get(`/tts/${res.data.key}/status`);
    if (res.data.status === "error") throw new Error(res.data.error || "Voice service failed");
  }
  return res.data.url;
}

export function SpeakButton({ text, language, label = "Read aloud", size = "default", className = "", prefetch = false }) {
  const [state, setState] = useState("idle");
  const [waitingLong, setWaitingLong] = useState(false);
  const audioRef = useRef(null);
  const cancelRef = useRef(false);

  const stop = () => {
    cancelRef.current = true;
    audioRef.current?.pause();
    audioRef.current = null;
    setState("idle");
    setWaitingLong(false);
  };

  useEffect(() => stop, []);

  useEffect(() => {
    if (prefetch && text?.trim()) apiClient.post("/tts", { text: text.slice(0, 4000), language, provider: "natural", prefetch: true }).catch(() => {});
  }, [prefetch, text, language]);

  const play = async (url) => {
    const audio = new Audio(`${API}${url}`);
    audioRef.current = audio;
    audio.onended = () => setState("idle");
    audio.onerror = () => { setState("idle"); toast.error("Could not play the audio"); };
    await audio.play();
    setState("playing");
    setWaitingLong(false);
  };

  const speak = async (provider = "natural") => {
    if (state === "playing") return stop();
    if (!text?.trim()) return toast.error("Nothing to read yet");
    cancelRef.current = false;
    setState("loading");
    const slowTimer = setTimeout(() => setWaitingLong(true), 6000);
    try {
      const url = await resolveAudio(text, language, provider, 150000);
      clearTimeout(slowTimer);
      if (cancelRef.current) return;
      if (!url) throw new Error("Voice is taking too long — try the quick voice");
      await play(url);
    } catch (e) {
      clearTimeout(slowTimer);
      if (cancelRef.current) return;
      setState("idle");
      setWaitingLong(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const quick = async () => {
    cancelRef.current = true;
    await sleep(50);
    cancelRef.current = false;
    setWaitingLong(false);
    setState("loading");
    try {
      const url = await resolveAudio(text, language, "quick", 40000);
      if (cancelRef.current) return;
      if (!url) throw new Error("Quick voice unavailable right now");
      await play(url);
    } catch (e) {
      if (cancelRef.current) return;
      setState("idle");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <Button data-testid="speak-btn" onClick={() => speak("natural")} size={size} variant="outline" disabled={state === "loading"}
        className={`border-stone-300 ${state === "playing" ? "bg-[#EAF6EF] text-[#2D6A4F] border-[#2D6A4F]" : ""}`}>
        {state === "loading" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : state === "playing" ? <Square className="h-4 w-4 mr-2" /> : <Volume2 className="h-4 w-4 mr-2" />}
        {state === "loading" ? (waitingLong ? "Preparing natural voice…" : "Preparing voice…") : state === "playing" ? "Stop" : label}
      </Button>
      {state === "loading" && waitingLong && (
        <Button data-testid="speak-quick-btn" onClick={quick} size={size} className="bg-[#D97706] hover:bg-[#78350F] text-white" title="Play a quick voice now (English-accent)">
          <Zap className="h-4 w-4 mr-1" /> Play quick voice
        </Button>
      )}
      {state === "loading" && (
        <button data-testid="speak-cancel-btn" onClick={stop} className="text-xs text-stone-500 hover:text-stone-800 px-1">Cancel</button>
      )}
    </div>
  );
}
